import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MAX_LINES, SANKEY_PALETTE } from './palette';
import { cssVar, useChart } from './useChart';
import { PLAN_ORDER, TotalsData } from './sections';

interface Series { days: string[]; events: Record<string, number[]> }
interface Props {
    series?: Series;
    seriesDays: number;
    categories: Array<[string, string[]]>;
    theme: 'light' | 'dark';
    rootRef: React.RefObject<HTMLDivElement>;
}

/** Interactive daily line chart. In-chart range chips slice the pre-fetched
 *  2×seriesDays series client-side — no refetch (that's what keeps it
 *  instant). Sticky palette slots: an event keeps its color while selected. */
export const OverTime: React.FC<Props> = ({ series, seriesDays, categories, theme, rootRef }) => {
    const presets = [7, 14, 30, 90].filter((r) => r <= seriesDays);
    const [range, setRange] = useState(Math.min(30, seriesDays));
    const [compare, setCompare] = useState(false);
    const [open, setOpen] = useState(false);
    const [query, setQuery] = useState('');
    const slotMap = useRef<Map<string, number>>(new Map());
    const [selected, setSelected] = useState<string[]>(() => {
        const evs = Object.keys(series?.events ?? {});
        const lastN = (arr: number[]) => arr.slice(-30).reduce((a, b) => a + b, 0);
        return evs.sort((a, b) => lastN(series!.events[b]) - lastN(series!.events[a])).slice(0, 4);
    });

    const slotFor = (ev: string): number => {
        const m = slotMap.current;
        if (m.has(ev)) return m.get(ev)!;
        const used = new Set(m.values());
        for (let s = 0; s < MAX_LINES; s++) {
            if (!used.has(s)) { m.set(ev, s); return s; }
        }
        return 0;
    };
    selected.forEach(slotFor);
    const color = (ev: string) => SANKEY_PALETTE[theme][slotFor(ev)];

    const canvasRef = useChart(() => {
        if (!series?.days?.length) return null;
        const N = series.days.length;
        const R = Math.min(range, N);
        const grid = cssVar(rootRef.current, '--chart-grid');
        const dim = cssVar(rootRef.current, '--fg-dim');
        const datasets: object[] = [];
        selected.forEach((ev) => {
            const arr = (series.events[ev]?.length ? series.events[ev] : new Array(N).fill(0));
            const c = color(ev);
            datasets.push({
                label: ev, data: arr.slice(N - R), borderColor: c, backgroundColor: c,
                fill: false, tension: 0.3, pointRadius: 0, borderWidth: 2,
            });
            if (compare && N >= 2 * R) {
                datasets.push({
                    label: `${ev} (prev)`, data: arr.slice(N - 2 * R, N - R),
                    borderColor: `${c}77`, backgroundColor: `${c}77`, fill: false,
                    tension: 0.3, pointRadius: 0, borderWidth: 1.5, borderDash: [5, 4],
                });
            }
        });
        return {
            type: 'line' as const,
            data: { labels: series.days.slice(N - R), datasets: datasets as never },
            options: {
                responsive: true, maintainAspectRatio: false,
                interaction: { mode: 'index' as const, intersect: false },
                color: dim,
                plugins: { legend: { position: 'bottom' as const, labels: { boxWidth: 10, boxHeight: 10, color: dim } } },
                scales: {
                    y: { beginAtZero: true, grid: { color: grid }, ticks: { color: dim } },
                    x: { grid: { display: false }, ticks: { color: dim, maxTicksLimit: 12, maxRotation: 0 } },
                },
            },
        };
    }, [series, range, compare, selected, theme]);

    // Every tracked event is selectable — an empty event is a finding, not a
    // hidden row. Events with no data in the loaded series render dimmed.
    const inSeries = series?.events ?? {};
    const categorized = new Set(categories.flatMap(([, evs]) => evs));
    const groups: Array<[string, string[]]> = [...categories];
    const other = Object.keys(inSeries).filter((e) => !categorized.has(e));
    if (other.length) groups.push(['Other', other]);
    const hasData = (ev: string) => (inSeries[ev] ?? []).some((v) => v > 0);
    const atCap = selected.length >= MAX_LINES;

    const toggleEvent = (ev: string, on: boolean) => {
        if (on) setSelected((s) => [...s, ev]);
        else {
            slotMap.current.delete(ev);
            setSelected((s) => s.filter((e) => e !== ev));
        }
    };

    return (
        <div className="pr-card">
            <h3>Feature usage — over time (daily events)</h3>
            <div className="pr-ctl-row">
                <span className="pr-ctl-label">Range</span>
                <div className="pr-ctl-group">
                    {presets.map((r) => (
                        <button key={r} type="button" className={`pr-chip ${range === r ? 'on' : ''}`}
                            onClick={() => setRange(r)}>{r}d</button>
                    ))}
                </div>
                <button type="button" className={`pr-chip ${compare ? 'on' : ''}`}
                    onClick={() => setCompare((c) => !c)}>Compare last period</button>
                <button type="button" className="pr-chip" onClick={() => setOpen((o) => !o)}>
                    Choose events ▾
                </button>
            </div>
            {open && (
                <div className="pr-ev-select">
                    <input className="pr-search" style={{ gridColumn: '1 / -1' }}
                        placeholder="Search events…" value={query}
                        onChange={(e) => setQuery(e.target.value)} />
                    {groups.map(([name, evs]) => {
                        const shown = evs.filter((ev) =>
                            ev.toLowerCase().includes(query.trim().toLowerCase()));
                        if (!shown.length) return null;
                        return (
                        <div className="pr-ev-group" key={name}>
                            <div className="pr-ev-cat">{name}</div>
                            {shown.map((ev) => {
                                const checked = selected.includes(ev);
                                return (
                                    <label className={`pr-ev-item ${!hasData(ev) ? 'nodata' : ''}`} key={ev}>
                                        <input type="checkbox" checked={checked}
                                            disabled={!checked && atCap}
                                            onChange={(e) => toggleEvent(ev, e.target.checked)} />
                                        <span className="dot"
                                            style={checked ? { background: color(ev) } : undefined} />
                                        <span className="pr-ev-name">{ev}</span>
                                        {!hasData(ev) && <span className="pr-ev-zero">0</span>}
                                    </label>
                                );
                            })}
                        </div>
                        );
                    })}
                </div>
            )}
            <div className="pr-chartbox tall"><canvas ref={canvasRef} /></div>
            <p className="pr-caption" style={{ marginTop: 8 }}>
                Up to {MAX_LINES} events at once (colors stay with an event while it remains
                selected). Dashed lines are the same events over the preceding period.
            </p>
        </div>
    );
};

interface Props2 {
    pairsBase?: Array<Record<string, unknown>>;
    pairsSeries?: Array<Record<string, unknown>>;
    seriesByPlan?: Array<{ event: string; day: string; plan: string; events: number }>;
    pairLabels: string[];
    series?: Series;
    seriesDays: number;
    theme: 'light' | 'dark';
    rootRef: React.RefObject<HTMLDivElement>;
    totals?: TotalsData;
    categories: Array<[string, string[]]>;
}

/** Event vs total: daily creations (bar) + cumulative-since-instrumentation
 *  (filled line). Cumulative = before_series + running sum of the daily series. */
export const EventVsTotal: React.FC<Props2> = ({
    pairsBase, pairsSeries, seriesByPlan, pairLabels, series, seriesDays, theme, rootRef, totals, categories,
}) => {
    const presets = [7, 14, 30, 90].filter((r) => r <= seriesDays);
    // Selection: one of the three property-based creation pairs, OR any tracked
    // editor event (its cumulative baseline is derived from the Totals data:
    // since-instrumentation total minus what the loaded series already covers).
    const [sel, setSel] = useState<{ kind: 'pair'; idx: number } | { kind: 'event'; ev: string }>(
        { kind: 'pair', idx: 0 },
    );
    const [range, setRange] = useState(Math.min(30, seriesDays));
    const [plan, setPlan] = useState('All');
    const [comboOpen, setComboOpen] = useState(false);
    const [comboQuery, setComboQuery] = useState('');
    const comboRef = useRef<HTMLDivElement | null>(null);
    useEffect(() => {
        if (!comboOpen) return undefined;
        const onDown = (e: MouseEvent) => {
            if (comboRef.current && !comboRef.current.contains(e.target as Node)) setComboOpen(false);
        };
        document.addEventListener('mousedown', onDown);
        return () => document.removeEventListener('mousedown', onDown);
    }, [comboOpen]);
    const pal = SANKEY_PALETTE[theme];
    const plans = useMemo(() => {
        const present = new Set((totals?.editor_events ?? []).map((r) => r.plan));
        return PLAN_ORDER.filter((p) => p === 'All' || present.has(p));
    }, [totals]);

    const label = sel.kind === 'pair' ? pairLabels[sel.idx] : sel.ev;
    // Both charts respect the plan filter: daily rows and cumulative baselines
    // are plan-split server-side; "All" sums across plans.
    const aligned = useMemo(() => {
        const days = series?.days ?? [];
        const idx = new Map(days.map((d, i) => [d, i]));
        const daily = new Array<number>(days.length).fill(0);
        const planMatch = (p: unknown) => plan === 'All' || String(p) === plan;
        let before = 0;
        if (sel.kind === 'pair') {
            (pairsSeries ?? []).forEach((r) => {
                if (!planMatch(r.plan)) return;
                const i = idx.get(String(r.day).slice(0, 10));
                if (i !== undefined) daily[i] += Number(r[`p${sel.idx}`] ?? 0);
            });
            (pairsBase ?? []).forEach((r) => {
                if (planMatch(r.plan)) before += Number(r[`p${sel.idx}_before`] ?? 0);
            });
        } else {
            (seriesByPlan ?? []).forEach((r) => {
                if (r.event !== sel.ev || !planMatch(r.plan)) return;
                const i = idx.get(r.day);
                if (i !== undefined) daily[i] += Number(r.events);
            });
            const inSeries = daily.reduce((a, b) => a + b, 0);
            const allTime = (totals?.editor_events ?? [])
                .filter((r) => r.event === sel.ev && planMatch(r.plan))
                .reduce((a, r) => a + r.events_all, 0);
            before = Math.max(0, (allTime || inSeries) - inSeries);
        }
        const cum: number[] = [];
        let run = before;
        daily.forEach((v) => { run += v; cum.push(run); });
        return { days, daily, cum };
    }, [series, pairsSeries, pairsBase, seriesByPlan, sel, plan, totals]);

    const N = aligned.days.length;
    const R = Math.min(range, N || range);
    const labels = aligned.days.slice(N - R);
    const chartOpts = (grid: string, dim: string, beginAtZero: boolean) => ({
        responsive: true, maintainAspectRatio: false,
        plugins: { legend: { display: false } },
        scales: {
            y: { beginAtZero, grid: { color: grid }, ticks: { color: dim } },
            x: { grid: { display: false }, ticks: { color: dim, maxTicksLimit: 12, maxRotation: 0 } },
        },
    });
    const dailyRef = useChart(() => {
        if (!N) return null;
        const grid = cssVar(rootRef.current, '--chart-grid');
        const dim = cssVar(rootRef.current, '--fg-dim');
        return {
            type: 'bar' as const,
            data: {
                labels,
                datasets: [{ label: `${label} / day`, data: aligned.daily.slice(N - R),
                    backgroundColor: pal[0], borderRadius: 3 }] as never,
            },
            options: chartOpts(grid, dim, true),
        };
    }, [aligned, range, theme]);
    const cumRef = useChart(() => {
        if (!N) return null;
        const grid = cssVar(rootRef.current, '--chart-grid');
        const dim = cssVar(rootRef.current, '--fg-dim');
        return {
            type: 'line' as const,
            data: {
                labels,
                datasets: [{ label: `Cumulative ${label}`, data: aligned.cum.slice(N - R),
                    borderColor: pal[2], backgroundColor: `${pal[2]}33`, fill: true,
                    tension: 0.2, pointRadius: 0, borderWidth: 2 }] as never,
            },
            options: chartOpts(grid, dim, false),
        };
    }, [aligned, range, theme]);

    const total = aligned.cum[aligned.cum.length - 1] ?? 0;
    return (
        <div className="pr-card">
            <h3>Event vs total — creations per day against the running total</h3>
            <div className="pr-ctl-row">
                <span className="pr-ctl-label">Series</span>
                <div className="pr-combo" ref={comboRef}>
                    <button type="button" className="pr-chip on"
                        onClick={() => { setComboOpen((o) => !o); setComboQuery(''); }}>
                        {label} ▾
                    </button>
                    {comboOpen && (
                        <div className="pr-combo-panel">
                            <input className="pr-search" autoFocus placeholder="Search series…"
                                value={comboQuery} onChange={(e) => setComboQuery(e.target.value)} />
                            {(() => {
                                const q = comboQuery.trim().toLowerCase();
                                const groups: Array<[string, Array<{ key: string; text: string; on: boolean; pick: () => void }>]> = [];
                                const pairItems = pairLabels
                                    .map((pl, i) => ({
                                        key: `pair:${i}`, text: pl,
                                        on: sel.kind === 'pair' && sel.idx === i,
                                        pick: () => setSel({ kind: 'pair', idx: i }),
                                    }))
                                    .filter((it) => it.text.toLowerCase().includes(q));
                                if (pairItems.length) groups.push(['Creation series (property-based)', pairItems]);
                                categories.forEach(([name, evs]) => {
                                    const items = evs
                                        .filter((ev) => ev.toLowerCase().includes(q))
                                        .map((ev) => ({
                                            key: `ev:${ev}`, text: ev,
                                            on: sel.kind === 'event' && sel.ev === ev,
                                            pick: () => setSel({ kind: 'event', ev }),
                                        }));
                                    if (items.length) groups.push([name, items]);
                                });
                                if (!groups.length) return <div className="pr-combo-empty">No series match.</div>;
                                return groups.map(([name, items]) => (
                                    <div key={name}>
                                        <div className="pr-combo-cat">{name}</div>
                                        {items.map((it) => (
                                            <button key={it.key} type="button"
                                                className={`pr-combo-item ${it.on ? 'on' : ''}`}
                                                onClick={() => { it.pick(); setComboOpen(false); }}>
                                                {it.text}
                                            </button>
                                        ))}
                                    </div>
                                ));
                            })()}
                        </div>
                    )}
                </div>
                <span className="pr-ctl-label">Plan</span>
                <div className="pr-ctl-group">
                    {plans.map((p) => (
                        <button key={p} type="button" className={`pr-chip ${plan === p ? 'on' : ''}`}
                            onClick={() => setPlan(p)}>{p}</button>
                    ))}
                </div>
                <span className="pr-ctl-label">Range</span>
                <div className="pr-ctl-group">
                    {presets.map((r) => (
                        <button key={r} type="button" className={`pr-chip ${range === r ? 'on' : ''}`}
                            onClick={() => setRange(r)}>{r}d</button>
                    ))}
                </div>
            </div>
            <div className="pr-caption">{label} — per day</div>
            <div className="pr-chartbox"><canvas ref={dailyRef} /></div>
            <div className="pr-caption" style={{ marginTop: 10 }}>
                {label} — cumulative total (now {total.toLocaleString('en-US')})
            </div>
            <div className="pr-chartbox"><canvas ref={cumRef} /></div>
            <p className="pr-caption" style={{ marginTop: 8 }}>
                Cumulative = events since instrumentation began, not all-time platform totals.
                “Google Docs created” under-counts: the create-new-Google-Doc flow doesn't fire
                document_created (known gap). The named creation series are property-based splits;
                any tracked editor event charts the same way. The plan filter applies to both
                charts (plan = the account's current plan, applied retroactively — it is a
                person property).
            </p>
        </div>
    );
};
